import { IUser } from '@common/types/user';
import { EventTypes, PublicOnSiteEvent, PublicWebinarEvent, RegistrationActions } from '@event/types/event';
import { useAppDispatch } from '@hooks/generic';
import { ButtonOwnProps } from '@mui/material';
import { useGetUserByGuestHashQuery } from '@services/accounts';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export interface RegistrationButtonStatus {
    disabled: boolean;
    text: string;
    tooltip?: string;
    action?: RegistrationActions;
    color: ButtonOwnProps['color'];
    backgroundColor: React.CSSProperties['backgroundColor'];
}

interface Props {
    isLoading: boolean;
    event?: PublicOnSiteEvent | PublicWebinarEvent;
    user?: Omit<IUser, 'full_name'>;
    isLoggedIn: boolean;
    guestAccessHash: string | null;
}

const useEventDetailScreenHandler = ({ isLoading, event, isLoggedIn, user, guestAccessHash }: Props) => {
    const [screen, setScreen] = useState<'card' | 'credentials'>();
    const [showGuestAccessBanner, setShowGuestAccessBanner] = useState(false);

    const {
        data: guestUserInfo,
        error: guestUserInfoError,
        isFetching: isGuestUserInfoFetching,
    } = useGetUserByGuestHashQuery(
        {
            guest_access_hash: guestAccessHash as string, // We can safely assume so because the call will be skipped if it's not truthy
        },
        {
            skip: !guestAccessHash,
            selectFromResult: (result) => ({
                ...result,
                data: guestAccessHash ? result.data : undefined,
            }),
        }
    );

    const { type } = useParams();
    const eventType = type as EventTypes;
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!isLoading && !isGuestUserInfoFetching) {
            // Is the event published?
            if (!event?.published) {
                setScreen('card');
                // Then render according to registration_status (Show unpublished card in this specific case)
            } else {
                // Is the user logged in?
                if (isLoggedIn && !showGuestAccessBanner) {
                    // In order for the user to not get stuck in a loop, if he choses the use the hash (he's still logged in), just check if the banner boolean was toggle on. If so, go to "else" to act as the hash flow
                    // Has the user access the event page using a Valid Guest Access Hash?
                    if (guestAccessHash && !guestUserInfoError && guestUserInfo) {
                        // Does the Guest Access Hash match the login with the user's login credentials?
                        if (guestUserInfo.email === user?.email) {
                            setScreen('card');
                            // Then render according to registration_status
                        } else {
                            setScreen('credentials');
                        }
                    } else {
                        // Then render according to registration_status
                        setScreen('card');
                    }
                } else {
                    // Has the user access the event page using a Valid Guest Access Hash?
                    if (guestAccessHash && !guestUserInfoError && guestUserInfo) {
                        setShowGuestAccessBanner(true);
                        setScreen('card');
                        // Then render according to registration_status
                    } else {
                        setShowGuestAccessBanner(false);
                        setScreen('card');
                        // Then render according to registration_status
                    }
                }
            }
        }
    }, [
        dispatch,
        event?.published,
        event?.registration_status,
        eventType,
        guestAccessHash,
        guestUserInfo,
        guestUserInfoError,
        isGuestUserInfoFetching,
        isLoading,
        isLoggedIn,
        showGuestAccessBanner,
        user,
    ]);

    return { screen, showGuestAccessBanner, setShowGuestAccessBanner };
};

export { useEventDetailScreenHandler };
